import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import { ToastProvider } from '@/components/common/Toast'
import { ToastHost, toast } from '@/lib/toast'
import { ConfirmHost } from '@/lib/confirm'
import { getApiErrorMessage } from '@/services/api'
import { useInstanceStore } from '@/stores/instanceStore'
import './index.css'

// Auto-reload on stale chunk errors after deploy
window.addEventListener('error', (e) => {
  const msg = e.message?.toLowerCase() ?? ''
  if (msg.includes('failed to fetch dynamically imported module') || msg.includes('importing a module script failed')) {
    const key = 'erp_chunk_reload'
    const last = sessionStorage.getItem(key)
    if (!last || Date.now() - Number(last) > 30_000) {
      sessionStorage.setItem(key, String(Date.now()))
      window.location.reload()
    }
  }
})

const queryClient = new QueryClient({
  // Feedback errore GLOBALE per le mutation: ogni mutation fallita mostra un toast,
  // a meno che non passi `meta: { silent: true }`. Elimina i fallimenti silenziosi.
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.meta?.silent) return
      toast.error(getApiErrorMessage(error))
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,   // 5 minutes
      gcTime: 1000 * 60 * 10,     // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
})

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Elemento root non trovato nel DOM')
}

// Legge /api/v1/instance (pubblica, senza auth) PRIMA del render: dice se
// questa installazione è completa (E-quipe) o una seconda istanza con solo
// alcuni moduli (es. PROXIMA). È risolta con timeout breve (vedi
// instanceStore.ts), quindi non blocca l'avvio in caso di backend
// irraggiungibile — ma va attesa prima di montare React, altrimenti la
// sidebar e le route cambierebbero "sotto" ai componenti già montati.
void useInstanceStore.getState().fetchInstance().finally(() => {
  createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ToastProvider>
            <App />
            <ToastHost />
            <ConfirmHost />
          </ToastProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>
  )
})
